import * as Yup from 'yup';

// Track form rules = the API's (CreateTrackDto), so a save is not refused
// after the modal looked fine (track-display-order T3): the old schema let
// through an order over 1000, 1-2 letter keywords and an empty icon or
// image, and the save's catch block then threw on a string rejection, so
// nothing was shown at all.

export const TRACK_ORDER_MAX = 1000;
export const TRACK_ORDER_TEXT = 'Display order must be a whole number from 0 to 1000';
export const TRACK_KEYWORD_TEXT = 'Each keyword needs at least 3 characters';

const URL_EXPRESSION = /^(ftp|http|https):\/\/[^ "]+$/;

export const trackSchema = Yup.object().shape({
  title: Yup.string()
    .trim()
    .min(3, 'Track title needs at least 3 characters')
    .max(128, 'Track title must be 128 characters or less')
    .required('Track title is required'),
  // Empty = the English title is sent (the API requires one).
  title_ar: Yup.string()
    .trim()
    .test('ar-title', 'Arabic title needs 3 to 128 characters', (v) => {
      const len = (v || '').length;
      return len === 0 || (len >= 3 && len <= 128);
    }),
  active: Yup.boolean(),
  isDiplomaLive: Yup.boolean(),
  isCareerTrack: Yup.boolean(),
  // Empty = 0 (as before); otherwise a whole number 0-1000.
  order: Yup.number()
    .transform((v, o) => (o === '' || o === null || o === undefined ? 0 : v))
    .typeError(TRACK_ORDER_TEXT)
    .integer(TRACK_ORDER_TEXT)
    .min(0, TRACK_ORDER_TEXT)
    .max(TRACK_ORDER_MAX, TRACK_ORDER_TEXT),
  certificateUrl: Yup.string(),
  description: Yup.string()
    .trim()
    .min(3, 'Description needs at least 3 characters')
    .required('Description is required'),
  category: Yup.string().required('Path is required'),
  image: Yup.string().required('Track image is required'),
  icon: Yup.string().required('Track icon is required'),
  // Optional, as the API has it: 13 of the 16 live tracks have no brief video,
  // and requiring one here made every edit to them (SOC, Web, Data…) fail
  // silently. A value that IS typed must still be a link.
  briefVideo: Yup.string().matches(URL_EXPRESSION, {
    message: 'Video Url must be a valid url',
    excludeEmptyString: true,
  }),
  keywords: Yup.array()
    .of(Yup.string().min(3, TRACK_KEYWORD_TEXT))
    .min(1, 'Add at least one keyword')
    .required('Add at least one keyword'),
  keywords_ar: Yup.array().of(Yup.string().min(3, TRACK_KEYWORD_TEXT)).nullable(),
});

/**
 * The POST / PATCH body from the validated form values. The form still sends
 * the whole stored track (a partial track PATCH would be refused by the
 * API's inherited initialisers, plan R5 X1); only these keys are adjusted.
 */
export function trackPayload(data: Record<string, any>): Record<string, any> {
  const body: Record<string, any> = { ...data };
  // title_ar is required by the API — fall back to the English title when the
  // Arabic title is left empty.
  if (!body.title_ar) body.title_ar = body.title;
  if (!body.description_ar) delete body.description_ar;
  if (!body.keywords_ar) delete body.keywords_ar;
  // The API validates `order` as a number; the input hands back a string.
  const order = Number(body.order);
  body.order = Number.isFinite(order) ? order : 0;
  return body;
}

/** The order as the form's text value: a stored 0 shows as "0", not blank. */
export const orderText = (order: unknown): string =>
  typeof order === 'number' && Number.isFinite(order) ? String(order) : '0';
